import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';

export const EASE = [0.22, 1, 0.36, 1];

// IntersectionObserver-based whileInView can miss a scroll change (this site
// runs Lenis smooth-scroll, which drives scroll position outside the normal
// native 'scroll' event path in some cases), leaving content permanently
// hidden mid-animation. Polling each frame with getBoundingClientRect is a
// few extra cheap reads until the element is revealed, then it stops itself
// — guaranteed correct regardless of how scroll is being driven.
function useRevealed(margin = 60) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (revealed) return undefined;
    const el = ref.current;
    if (!el) return undefined;

    let raf;
    const check = () => {
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const rect = el.getBoundingClientRect();
      if (vh && rect.top < vh - margin && rect.bottom > 0) {
        setRevealed(true);
        return;
      }
      raf = requestAnimationFrame(check);
    };
    raf = requestAnimationFrame(check);

    return () => cancelAnimationFrame(raf);
  }, [revealed, margin]);

  return [ref, revealed];
}

export const Page = ({ children, className = '' }) => (
  <motion.main
    className={className}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: -12 }}
    transition={{ duration: 0.5, ease: EASE }}
  >
    {children}
  </motion.main>
);

export const Reveal = ({ children, className = '', delay = 0, y = 36 }) => {
  const [ref, revealed] = useRevealed(60);
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y }}
      animate={revealed ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.8, delay, ease: EASE }}
    >
      {children}
    </motion.div>
  );
};

const MaskedLine = ({ line, delay, lineClassName, inView }) => {
  const [ref, revealed] = useRevealed(40);
  const show = inView ? revealed : true;
  return (
    <span ref={ref} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        className={`block ${lineClassName}`}
        initial={{ y: '115%' }}
        animate={show ? { y: '0%' } : undefined}
        transition={{ duration: 0.9, delay, ease: EASE }}
      >
        {line}
      </motion.span>
    </span>
  );
};

export const MaskedLines = ({ lines, className = '', lineClassName = '', delay = 0, inView = false }) => (
  <div className={className}>
    {lines.map((line, i) => (
      <MaskedLine key={i} line={line} delay={delay + i * 0.12} lineClassName={lineClassName} inView={inView} />
    ))}
  </div>
);

export const SectionTag = ({ number, label, light = false }) => {
  const [ref, revealed] = useRevealed(60);
  return (
    <motion.div
      ref={ref}
      className="flex items-center gap-4 mb-8"
      initial={{ opacity: 0, y: 16 }}
      animate={revealed ? { opacity: 1, y: 0 } : undefined}
      transition={{ duration: 0.8, ease: EASE }}
    >
      <span className={`font-display text-sm font-medium ${light ? 'text-ink' : 'text-accent'}`}>{number}</span>
      <motion.span
        className="h-px w-10 bg-accent origin-left"
        aria-hidden="true"
        initial={{ scaleX: 0 }}
        animate={revealed ? { scaleX: 1 } : undefined}
        transition={{ duration: 0.5, delay: 0.15, ease: EASE }}
      />
      <span className={`text-xs uppercase tracking-[0.25em] ${light ? 'text-ink/55' : 'text-mute'}`}>{label}</span>
    </motion.div>
  );
};
